import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import OptionGroupList from '../features/options/OptionGroupList';
import OptionGroupEditor from '../features/options/OptionGroupEditor';
import { deleteOptionGroup, listOptionGroups, getOptionGroup } from '../services/options';
import { listSizeTemplates, listItemGroups, listCategories, listProducts } from '../services/catalog';

export default function OptionsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const { data: optionGroups = [] } = useQuery({
    queryKey: ['options', 'groups'],
    queryFn: listOptionGroups,
  });

  const { data: optionGroupDetail } = useQuery({
    queryKey: ['options', 'group-detail', selectedGroupId],
    queryFn: () => (selectedGroupId ? getOptionGroup(selectedGroupId) : Promise.resolve(null)),
    enabled: Boolean(selectedGroupId),
  });

  const { data: sizeTemplates = [] } = useQuery({
    queryKey: ['catalog', 'size-templates'],
    queryFn: listSizeTemplates,
  });

  const { data: itemGroups = [] } = useQuery({
    queryKey: ['catalog', 'item-groups'],
    queryFn: listItemGroups,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: listCategories,
  });
  const { data: products = [] } = useQuery({
    queryKey: ['catalog', 'products', 'options'],
    queryFn: listProducts,
  });

  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteOptionGroup(groupId);
      await queryClient.invalidateQueries({ queryKey: ['options', 'groups'] });
      await queryClient.invalidateQueries({
        queryKey: ['options', 'group-detail', groupId],
      });
      if (selectedGroupId === groupId) {
        setSelectedGroupId(null);
        setIsCreating(false);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('group');
        setSearchParams(nextParams);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Opsiyon silinemedi.';
      window.alert(message);
    }
  };

  const handleSelect = (groupId: string | null) => {
    setIsCreating(false);
    setSelectedGroupId(groupId);
    const nextParams = new URLSearchParams(searchParams);
    if (groupId) {
      nextParams.set('group', groupId);
    } else {
      nextParams.delete('group');
    }
    setSearchParams(nextParams);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setSelectedGroupId(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('group');
    setSearchParams(nextParams);
  };

  const editorGroup = isCreating ? null : optionGroupDetail ?? null;

  useEffect(() => {
    const groupParam = searchParams.get('group');
    if (groupParam && groupParam !== selectedGroupId) {
      setSelectedGroupId(groupParam);
      setIsCreating(false);
    }
    if (!groupParam && !isCreating && !selectedGroupId) {
      // no-op
    }
  }, [searchParams, selectedGroupId, isCreating]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Opsiyon Yönetimi</h1>
          <p>Hamur, sos ve ekstra malzemeleri düzenleyin.</p>
        </div>
      </header>

      <section className="page-section">
        <div className="card-grid" style={{ gap: '1.5rem' }}>
          <OptionGroupList
            groups={optionGroups}
            selectedId={isCreating ? null : selectedGroupId}
            onSelect={handleSelect}
            onDelete={(group) => {
              if (
                window.confirm(
                  `${group.name} opsiyon grubunu silmek istediğinize emin misiniz?`,
                )
              ) {
                void handleDeleteGroup(group.id);
              }
            }}
            onCreate={handleCreate}
            canClear={isCreating || Boolean(selectedGroupId)}
          />

          <OptionGroupEditor
            optionGroup={editorGroup}
            isCreating={isCreating}
            sizeTemplates={sizeTemplates}
            itemGroups={itemGroups}
            categories={categories}
            products={products}
            onSaved={(groupId) => {
              setSelectedGroupId(groupId);
              setIsCreating(false);
              void queryClient.invalidateQueries({
                queryKey: ['options', 'group-detail', groupId],
              });
            }}
            onCreated={(groupId) => {
              setIsCreating(false);
              setSelectedGroupId(groupId);
              void queryClient.invalidateQueries({
                queryKey: ['options', 'group-detail', groupId],
              });
            }}
            onDeleted={() => {
              setSelectedGroupId(null);
              setIsCreating(false);
            }}
          />
        </div>
      </section>
    </div>
  );
}
